import React, { useContext, useMemo, useState, useEffect } from "react";
import { SharedDrive, SyncStatus } from "~/gql/types2";
import { AnyFolder, StructuralFolder } from "./types";
import {
  useGetProjectFoldersQuery,
  useGetSharedDrivesQuery,
} from "./query.hotchoc.generated";
import { organizeQueryResults } from "./organizeQueryResults";

export type ProjectFoldersPageApi = Readonly<{
  topLevelFolders: StructuralFolder[];
  sharedDrives: SharedDrive[];
  loading: boolean;
  reload: React.DispatchWithoutAction;
  selectedFolder: AnyFolder | null;
  setSelectedFolder: React.Dispatch<AnyFolder | null>;
  projectManager: string | null;
  setProjectManager: React.Dispatch<string | null>;
}>;

const ProjectFoldersApiContext =
  React.createContext<ProjectFoldersPageApi | null>(null);

export const ProjectFoldersApiProvider: React.FC<{
  api: ProjectFoldersPageApi;
}> = ({ api, children }) => {
  return (
    <ProjectFoldersApiContext.Provider value={api}>
      {children}
    </ProjectFoldersApiContext.Provider>
  );
};

/**
 * Gets the API in the context; or, if there is no context, creates
 * a new API.
 */
export function useProjectFoldersPageApi(): ProjectFoldersPageApi {
  const existingApi = useContext(ProjectFoldersApiContext);

  if (existingApi) {
    return existingApi;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useCreatedApi();
}

function useCreatedApi(): ProjectFoldersPageApi {
  const [selectedFolder, setSelectedFolder] = useState<AnyFolder | null>(null);
  const [projectManager, setProjectManager] = useState<string | null>(null);

  const {
    data: folders,
    loading: foldersLoading,
    refetch,
  } = useGetProjectFoldersQuery({
    variables: { input: { projectManager: "" } },
  });

  const { data: drives, loading: drivesLoading } = useGetSharedDrivesQuery();

  const loading = foldersLoading || drivesLoading;

  const rawResults = folders?.projectFolders?.folders as StructuralFolder[];
  const sharedDrives = drives?.projectFolders?.drives as SharedDrive[];

  const topLevelFolders = useMemo(
    () => organizeQueryResults(rawResults ?? []),
    [rawResults]
  );

  function reload() {
    setSelectedFolder(null);
    void refetch({ input: { projectManager: projectManager } });
  }

  // Helper to recursively check for any pending sync status
  function hasPendingSync(folder: AnyFolder): boolean {
    if (
      folder.__typename !== "StructuralFolder" &&
      (folder.syncStatus === SyncStatus.InProcess ||
        folder.syncStatus === SyncStatus.Unsynced)
    ) {
      return true;
    }
    if (folder.__typename === "StructuralFolder" && folder.clientFolders) {
      return folder.clientFolders.some(hasPendingSync);
    }
    if (
      folder.__typename === "CustomerFolder" &&
      (folder.projectFolders || folder.groupedFolders)
    ) {
      return (
        folder.projectFolders.some(hasPendingSync) ||
        folder.groupedFolders.some(hasPendingSync)
      );
    }
    return false;
  }

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    const anyPending = topLevelFolders.some(hasPendingSync);
    if (anyPending) {
      timer = setInterval(() => {
        reload();
      }, 60000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [topLevelFolders]);

  useEffect(() => {
    reload();
  }, [projectManager]);

  return {
    selectedFolder,
    setSelectedFolder,
    projectManager,
    setProjectManager,
    loading,
    topLevelFolders,
    sharedDrives,
    reload,
  };
}
